import React, { useEffect } from 'react'
import { IonApp, setupIonicReact } from '@ionic/react'
import { IonReactRouter } from '@ionic/react-router'
import AppRouter from './components/appRouter/AppRouter'
import './app.css'
import '@ionic/react/css/core.css'
import '@ionic/react/css/normalize.css'
import '@ionic/react/css/structure.css'
import '@ionic/react/css/typography.css'
import '@ionic/react/css/padding.css'
import '@ionic/react/css/float-elements.css'
import '@ionic/react/css/text-alignment.css'
import '@ionic/react/css/text-transformation.css'
import '@ionic/react/css/flex-utils.css'
import '@ionic/react/css/display.css'
import './theme-variables.css'
import './dark-theme-variables.css'
import './styles/index.css'
import NotificationsPopup from './components/notification/NotificationsPopup.component'
import { App as CapacitorApp, URLOpenListenerEvent } from '@capacitor/app'
import ApiErrorHandler from './components/apiErrorHandler/ApiErrorHandler.component'
import MenuComponent from './components/menu/Menu.component'
import { AlertComponent } from './components/alert/Alert.component'
import Toast from './components/toast/Toast.component'
import PNNVNativeKeyboardWrapper from './components/pnnv/PNNVNativeKeyboardWrapper.component'
import SessionResumeModal from './features/session/resume/components/SessionResumeModal'
import { useHistory } from 'react-router-dom'
import { goBack, setRouterHistory } from './utilities/navigation.util'
import { setLoginCallbackUrl } from './features/login/store/login.actions'
import { useDispatch } from 'react-redux'
import Logger from './services/logger.service'
import { PluginListenerHandle } from '@capacitor/core'
import { defineCustomElements } from '@ionic/pwa-elements/loader'
import { useGlobalIonItemFocus } from './hooks/useGlobalIonItemFocus'
import ErrorBoundaryWrapper from './components/errorBoundry/ErrorBoundry.component'
import { UserpilotScreenTracker } from './features/userPilot/UserpilotScreenTracker'
import { isResumeSessionEnabled } from './features/session/resume/utils/sessionResume.util'
import Loader from './components/loader/Loader.component'
import { useAppForegroundHandler } from './features/session/persist/hooks/useAppForegroundHandler'
import SessionPersistModal from './features/session/persist/components/SessionPersistModal'
import SessionPersistGate from './features/session/persist/components/SessionPersistGate'

setupIonicReact({ mode: 'ios', swipeBackEnabled: false, hardwareBackButton: false, animated: false })

const HistoryInitializer: React.FC = () => {
  const history = useHistory()
  setRouterHistory(history)
  return null
}
// Ensure PWA Elements are loaded
defineCustomElements(window)

const App: React.FC = () => {
  const dispatch = useDispatch()
  useAppForegroundHandler()

  useGlobalIonItemFocus()

  useEffect(() => {
    let backButtonListener: PluginListenerHandle | null = null
    let appUrlOpenListener: PluginListenerHandle | null = null

    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      const openNumberPadModal: HTMLIonModalElement | null = document.querySelector(
        `ion-modal[data-testid='NumberPadModal']`
      )
      const openNumberPadPopover: HTMLIonPopoverElement | null = document.querySelector(
        `ion-popover[data-testid='NumberPadPopover']`
      )

      if (openNumberPadModal) {
        openNumberPadModal.dismiss()
      } else if (openNumberPadPopover) {
        openNumberPadPopover.dismiss()
      }

      if (!canGoBack) {
        CapacitorApp.exitApp()
      } else {
        goBack()
      }
    }).then((listener) => {
      backButtonListener = listener
    })

    CapacitorApp.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      const url = event.url
      Logger.info(`appUrlOpen event triggered for URL: ${url}`)

      if (url.includes('login/callback')) {
        dispatch(setLoginCallbackUrl(url))
      }
    }).then((listener) => {
      appUrlOpenListener = listener
    })

    return () => {
      backButtonListener?.remove()
      appUrlOpenListener?.remove()
    }
  }, [dispatch])

  window.onerror = (message?: string | Event, source?: string, lineno?: number, colno?: number, error?: Error) => {
    Logger.error(`Global Error Caught: ${message}, ${source}, ${lineno}, ${colno}, ${error}`)
  }

  return (
    <IonApp>
      <IonReactRouter>
        <HistoryInitializer />
        <UserpilotScreenTracker />
        <ErrorBoundaryWrapper>
          <SessionPersistGate>
            <AppRouter />
            <MenuComponent />
          </SessionPersistGate>
          <ApiErrorHandler />
          {isResumeSessionEnabled() && <SessionResumeModal />}
        </ErrorBoundaryWrapper>
      </IonReactRouter>
      <AlertComponent />
      <Toast />
      <Loader />
      <NotificationsPopup />
      <SessionPersistModal />

      <PNNVNativeKeyboardWrapper />
    </IonApp>
  )
}

export default App
